import qs from 'qs';
import { term } from 'lucene';
import { Filter, GroupFilterMode, SearchQuery, Sort } from './types';
import { LocationQuery, LocationQueryValue } from 'vue-router';

export const escapeSearchSpecialCharacters = (value: string): string => term.escape(value);

export const getFilterString = (filter: any): string[] => {
  const filterDelimiter = ':';

  return Object.getOwnPropertyNames(filter).flatMap((prop) => {
    let value = filter[prop];
    if (prop === 'ages') {
      return [`${value}`];
    }
    if (prop === 'languages.audio' && typeof value === 'string') {
      value = value.split(',');
    }
    if (Array.isArray(value)) {
      return (value as string[]).map((val) => `${prop}${filterDelimiter}${val}`);
    }

    return [`${prop}${filterDelimiter}${value}`];
  });
};

export const getQueryString = (params: SearchQuery): string => {
  let filter: any = [];

  if (params?.filter !== undefined) {
    // JSON.parse(JSON.stringify()) is to remove the observer object from the list that vue store adds
    filter = [...filter, ...getFilterString(JSON.parse(JSON.stringify(params.filter)))];
  }

  const query = {
    q: params.q,
    searchFields: params.searchFields,
    filter,
    sort: params.sort?.map((fieldSortParam) => `${fieldSortParam.field}:${fieldSortParam.order ?? 'desc'}`),
    pagination: params.pagination ?? { size: 20 },
    groupFilterMode: params.groupFilterMode,
    randomSeed: params.randomSeed,
  };

  const urlQs = qs.stringify(query, { arrayFormat: 'repeat' });

  return urlQs ? `?${urlQs}` : '';
};

const getSortFromSortString = (sortString: string): Sort => {
  const [field, order] = sortString.split(':');

  if (!field || !order) {
    throw new Error(`Invalid sort string: ${sortString}`);
  }
  return { field, order: order as 'asc' | 'desc' };
};

const getSortFromSortStrings = (sortStrings: string[]): Sort[] | undefined =>
  sortStrings.length > 0 ? sortStrings.map(getSortFromSortString) : undefined;

const getKeyValueFromFilterToken = (filterToken: string): [string, string] => {
  if (filterToken.startsWith('age(')) {
    return ['ages', filterToken];
  }

  // In case there is a  ":" in the value, we need to split the string at the first occurrence of ":"
  const firstColonIndex = filterToken.indexOf(':');
  const key = filterToken.substring(0, firstColonIndex);
  const value = filterToken.substring(firstColonIndex + 1);
  return [key, value];
};

const getFilterFromFilterTokens = (filterTokens: string[]): Filter => {
  const filter: Filter = {};

  // A key can be repeated (e.g. filter=languages:sv&filter=languages:en), so we should collect its values into an array
  for (const filterToken of filterTokens) {
    const [key, value] = getKeyValueFromFilterToken(filterToken);
    const existingValue = filter[key];

    if (existingValue === undefined) {
      // First value for this key, store as a plain string e.g. { languages: 'sv' }
      filter[key] = value;
    } else if (Array.isArray(existingValue)) {
      // Key already contains an array of values, push the new value e.g. { languages: ['sv', 'en'] } → ['sv', 'en', 'de']
      existingValue.push(value);
    } else {
      // Second value for this key, convert it to an array e.g. { languages: 'sv' } → { languages: ['sv', 'en'] }
      filter[key] = [existingValue as string, value];
    }
  }
  return filter;
};

export const getQueryObject = (query: string): SearchQuery => {
  const parsed = qs.parse(query);
  let filter: Filter = {};

  if (parsed.filter !== undefined) {
    const filterTokens = Array.isArray(parsed.filter) ? parsed.filter : [parsed.filter];
    filter = getFilterFromFilterTokens(filterTokens as string[]);
  }

  const sort: Sort[] = [];
  if (parsed.sort) {
    if (Array.isArray(parsed.sort)) {
      sort.push(
        ...(parsed.sort as string[]).map((sortString: string) => {
          return getSortFromSortString(sortString);
        }),
      );
    } else if (typeof parsed.sort === 'string') {
      sort.push(getSortFromSortString(parsed.sort));
    }
  }
  return { ...parsed, filter, sort: sort.length > 0 ? sort : undefined };
};

export const getAggregateString = (
  aggregateFields: string[],
  globalFilters: Filter,
  size?: number,
  query?: string,
  groupFilterMode?: GroupFilterMode,
) => {
  let filter: any = [];

  if (globalFilters !== undefined) {
    // JSON.parse(JSON.stringify()) is to remove the observer object from the list that vue store adds
    filter = [...filter, ...getFilterString(JSON.parse(JSON.stringify(globalFilters)))];
  }
  const urlQs = qs.stringify(
    { filter, dim: aggregateFields, size, q: query, groupFilterMode },
    { arrayFormat: 'repeat' },
  );
  return urlQs ? `?${urlQs}` : '';
};

// Normalizes a route query value (string, array, null or undefined) to a string array, dropping null and undefined
const toStrings = (value: LocationQueryValue | LocationQueryValue[] | undefined): string[] =>
  (Array.isArray(value) ? value : [value]).filter((item): item is string => typeof item === 'string');

const isGroupFilterMode = (value?: string): value is GroupFilterMode => value === 'and' || value === 'or';

export const getSearchQueryFromRoute = (query: LocationQuery): SearchQuery => {
  const [q] = toStrings(query.q);
  const searchFields = toStrings(query.searchFields);
  const [groupFilterMode] = toStrings(query.groupFilterMode);
  const filter = toStrings(query.filter);
  const sort = toStrings(query.sort);

  return {
    q,
    searchFields: searchFields.length > 0 ? searchFields : undefined,
    filter: getFilterFromFilterTokens(filter),
    sort: getSortFromSortStrings(sort),
    groupFilterMode: isGroupFilterMode(groupFilterMode) ? groupFilterMode : undefined,
  };
};
